import request from '../../assets/utils/http-request';
import config from '../../assets/utils/config';

const getAuthHeaders = () => {
  const token = localStorage.getItem(config.key.token);
  const defaultHeaders = request.defaults?.headers?.common || {};

  return {
    headers: {
      ...defaultHeaders,
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  };
};

const withAuthConfig = (configOverride = {}) => {
  const authHeaders = getAuthHeaders();
  return {
    ...authHeaders,
    ...configOverride,
    headers: {
      ...authHeaders.headers,
      ...(configOverride.headers || {}),
    },
  };
};

export const fetchCurrentUserProfile = async (tokenOverride = null) => {
  // If token is provided, use it directly; otherwise use withAuthConfig
  const config = tokenOverride
    ? { headers: { Authorization: `Bearer ${tokenOverride}` } }
    : withAuthConfig();
  const response = await request.get('/v2/users/profile', config);
  return response?.data;
};

/**
 * Cheap "did my permissions change" check — { version, updated_at } — so a
 * live session can detect a role/permission change without refetching the
 * full profile every poll tick. Compare `version` to the last value seen and
 * call refreshProfile() only when it differs.
 */
export const fetchPermissionsVersion = async () => {
  const response = await request.get('/v2/users/permissions-version', withAuthConfig());
  return response?.data;
};

/** Self-service profile update (email, full_name, phone). */
export const updateOwnProfile = async (payload) => {
  const response = await request.patch('/v2/users/profile', payload, withAuthConfig());
  return response?.data;
};

/** Self-service password change. Requires current_password. */
export const changeOwnPassword = async (payload) => {
  const response = await request.put('/v2/users/profile/password', payload, withAuthConfig());
  return response?.data;
};

/** Admin update of care-admin staff by id. */
export const updateStaffUser = async (staffId, payload = {}) => {
  const response = await request.patch(
    `/v2/users/${staffId}`,
    { user_type: 'staff', ...payload },
    withAuthConfig()
  );
  return response?.data;
};

/** Admin set staff password (no current password). */
export const changeStaffPassword = async (staffId, payload = {}) => {
  const response = await request.put(
    `/v2/users/${staffId}/password`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

/** Create a staff account via the v2 users route. Staff creation must NOT go
 * through the legacy ?aCreateUser front controller: its JSON body (plaintext
 * password + email) trips an upstream WAF filter that answers 406 before the
 * request ever reaches PHP. */
export const createStaffUser = async (payload = {}) => {
  const response = await request.post('/v2/users/', { user_type: 'staff', ...payload }, withAuthConfig());
  return response?.data;
};

export const fetchStaffUsers = async (params = {}) => {
  // Ensure user_type=staff and include_roles=true for staff listing
  const queryParams = {
    user_type: 'staff',
    include_roles: true,
    ...params,
  };
  const response = await request.get('/v2/users/', withAuthConfig({ params: queryParams }));
  return response?.data;
};

/**
 * GET /v2/rbac/roles defaults to 20 per page and caps at 100. These are the
 * pickers an admin assigns from, so a silently truncated page reads as "that
 * role cannot be assigned" — ask for the cap, not the default.
 */
const ROLE_PICKER_LIMIT = 100;

export const fetchStaffRoles = async (query = {}) => {
  const response = await request.get('/v2/rbac/roles', withAuthConfig({
    params: { user_type: 'staff', limit: ROLE_PICKER_LIMIT, ...query },
  }));
  return response?.data;
};

export const listRoles = async (query = {}) => {
  const response = await request.get('/v2/rbac/roles', withAuthConfig({
    params: { limit: ROLE_PICKER_LIMIT, ...query },
  }));
  return response?.data;
};

export const fetchPermissions = async (query = {}) => {
  const response = await request.get('/v2/rbac/permissions', withAuthConfig({
    params: query,
  }));
  return response?.data;
};

export const assignRoleToStaff = async (userId, payload) => {
  const response = await request.post(
    `/v2/rbac/users/staff/${userId}/roles`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const removeRoleFromStaff = async (userId, roleId) => {
  const response = await request.delete(
    `/v2/rbac/users/staff/${userId}/roles/${roleId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const assignRoleToFacilityUser = async (userId, payload) => {
  const response = await request.post(
    `/v2/rbac/users/facility/${userId}/roles`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const removeRoleFromFacilityUser = async (userId, roleId) => {
  const response = await request.delete(
    `/v2/rbac/users/facility/${userId}/roles/${roleId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const createRole = async (payload) => {
  const response = await request.post(
    '/v2/rbac/roles',
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const fetchPermissionPacks = async () => {
  const response = await request.get('/v2/rbac/packs', withAuthConfig());
  return response?.data;
};

export const applyPermissionPackToRole = async (roleId, packKey) => {
  const response = await request.post(
    `/v2/rbac/roles/${roleId}/packs/${packKey}`,
    {},
    withAuthConfig()
  );
  return response?.data;
};

export const assignPermissionsToRole = async (roleId, payload) => {
  const response = await request.post(
    `/v2/rbac/roles/${roleId}/permissions`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const updateRole = async (roleId, payload) => {
  const response = await request.put(
    `/v2/rbac/roles/${roleId}`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const deleteRole = async (roleId) => {
  const response = await request.delete(
    `/v2/rbac/roles/${roleId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const getRoleDetails = async (roleId) => {
  const response = await request.get(
    `/v2/rbac/roles/${roleId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const getPermissionDetails = async (permissionId) => {
  const response = await request.get(
    `/v2/rbac/permissions/${permissionId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const toggleRoleStatus = async (roleId, status) => {
  const response = await request.patch(
    `/v2/rbac/roles/${roleId}/status`,
    { status },
    withAuthConfig()
  );
  return response?.data;
};

export const toggleRoleSystemFlag = async (roleId, isSystemRole) => {
  const response = await request.patch(
    `/v2/rbac/roles/${roleId}/system-role`,
    { is_system_role: isSystemRole },
    withAuthConfig()
  );
  return response?.data;
};

/*
 * Per-user permission grants.
 *
 * Gives one person one extra capability without editing shared roles. The
 * backend implements each grant as a role holding exactly that permission, so
 * there is still a single resolution path; see
 * sqls/migrations/20260902_rbac_permission_grants.sql
 */

/** Permissions granted to this user directly, separate from role-derived access. */
export const listUserPermissionGrants = async (userType, userId) => {
  const response = await request.get(
    `/v2/rbac/users/${encodeURIComponent(userType)}/${encodeURIComponent(userId)}/permissions`,
    withAuthConfig()
  );
  return response?.data;
};

/**
 * Grant one permission to one user.
 * Prefer expires_in_minutes over expires_at: expiry is compared against the
 * database clock, and a timestamp built in the browser is wrong whenever the
 * browser and the server disagree on timezone.
 */
export const grantUserPermission = async (userType, userId, payload) => {
  const response = await request.post(
    `/v2/rbac/users/${encodeURIComponent(userType)}/${encodeURIComponent(userId)}/permissions`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

/** Remove this user's grant. The backing role stays for anyone else holding it. */
export const revokeUserPermission = async (userType, userId, permission) => {
  const response = await request.delete(
    `/v2/rbac/users/${encodeURIComponent(userType)}/${encodeURIComponent(userId)}/permissions/${encodeURIComponent(permission)}`,
    withAuthConfig()
  );
  return response?.data;
};

/*
 * Permission catalog — which product module can carry which actions.
 * Backs the module/action pickers on the New-permission form so a slug is
 * chosen from a list instead of typed. Backend: /v2/rbac/permission-modules.
 */

/** Modules with per-module counts (action_count / assigned_count / unassigned_count). */
export const listPermissionModules = async (params = {}) => {
  const response = await request.get(
    '/v2/rbac/permission-modules',
    withAuthConfig({ params })
  );
  return response?.data;
};

/**
 * Actions in a module. availability='unused' returns only actions that do not
 * already have a permission, which is what the create form asks for.
 * Pass moduleKey 'all' to list across every module.
 */
export const listModuleActions = async (moduleKey, availability = 'all') => {
  const response = await request.get(
    `/v2/rbac/permission-modules/${encodeURIComponent(moduleKey)}/actions`,
    withAuthConfig({ params: { availability } })
  );
  return response?.data;
};

/** Add a module to the catalog. */
/**
 * Which roles grant which permission — the reverse of every other RBAC read.
 * One request for the whole map; the catalog screen needs every permission at
 * once and N+1 over ~115 of them is not a screen anyone waits for.
 */
export const listPermissionRoles = async () => {
  const response = await request.get('/v2/rbac/permission-roles', withAuthConfig());
  return response?.data;
};

export const createPermissionModule = async (payload) => {
  const response = await request.post(
    '/v2/rbac/permission-modules',
    payload,
    withAuthConfig()
  );
  return response?.data;
};

/** Add an action to a module, so a permission can then be created for it. */
export const createModuleAction = async (moduleKey, payload) => {
  const response = await request.post(
    `/v2/rbac/permission-modules/${encodeURIComponent(moduleKey)}/actions`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const createPermissionRecord = async (payload) => {
  const response = await request.post(
    '/v2/rbac/permissions',
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const updatePermissionRecord = async (permissionId, payload) => {
  const response = await request.put(
    `/v2/rbac/permissions/${permissionId}`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const deletePermissionRecord = async (permissionId) => {
  const response = await request.delete(
    `/v2/rbac/permissions/${permissionId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const togglePermissionStatus = async (permissionId, status) => {
  const response = await request.patch(
    `/v2/rbac/permissions/${permissionId}/status`,
    { status },
    withAuthConfig()
  );
  return response?.data;
};

export const removePermissionFromRole = async (roleId, permissionId) => {
  const response = await request.delete(
    `/v2/rbac/roles/${roleId}/permissions/${permissionId}`,
    withAuthConfig()
  );
  return response?.data;
};

export const runRbacSeeder = async (payload = { confirm: 'RBAC_SEED_NOW' }) => {
  const response = await request.post(
    '/v2/rbac/seed/run',
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const verifyRbacSystem = async () => {
  const response = await request.get(
    '/v2/rbac/seed/verify',
    withAuthConfig()
  );
  return response?.data;
};

export const cleanupRbacSystem = async (scope = 'assignments') => {
  const response = await request.post(
    '/v2/rbac/seed/clean',
    {
      confirm: 'RBAC_CLEAN_NOW',
      scope,
    },
    withAuthConfig()
  );
  return response?.data;
};

export const fetchAccessModules = async (query = {}) => {
  const response = await request.get('/v2/rbac/access-modules', withAuthConfig({
    params: query,
  }));
  return response?.data;
};

export const createAccessModule = async (payload) => {
  const response = await request.post(
    '/v2/rbac/access-modules',
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const updateAccessModule = async (moduleId, payload) => {
  const response = await request.put(
    `/v2/rbac/access-modules/${moduleId}`,
    payload,
    withAuthConfig()
  );
  return response?.data;
};

export const deleteAccessModule = async (moduleId) => {
  const response = await request.delete(
    `/v2/rbac/access-modules/${moduleId}`,
    withAuthConfig()
  );
  return response?.data;
};




